import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, Avatar, 
  IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
  SmartToy, Close, PersonAdd, AddCircle, DragIndicator, Circle,
  LocalHospital, Person, Support, Science, Analytics, Event, Minimize
} from '@mui/icons-material';
import MammoAssistChat from '../../components/MammoAssistChat';

const MammoAssistDialog = ({ 
  open, 
  onClose, 
  onMinimize,
  user = null, 
  currentPortal = 'landing', 
  connectionStatus = { fastapi: 'disconnected', graphql: 'disconnected', mcp: 'disconnected' },
  addUserMenuAnchor = null,
  setAddUserMenuAnchor = () => {},
  addAgentMenuAnchor = null,
  setAddAgentMenuAnchor = () => {},
  handleAddUser = () => {},
  handleAddAgent = () => {},
  getConnectionStatusColor = (status) => status === 'connected' ? '#4caf50' : '#f44336'
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleMinimize = () => {
    if (onMinimize) {
      onMinimize();
    }
  };

  return (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="lg"
    fullWidth
    PaperProps={{
      sx: { 
        height: '85vh', 
        maxHeight: '700px',
        cursor: isDragging ? 'grabbing' : 'default',
        position: 'fixed',
        margin: 'auto',
        maxWidth: '900px',
        transition: isDragging ? 'none' : 'all 0.2s ease'
      },
      // Make dialog draggable
      onMouseDown: (e) => {
        // Only allow dragging from the title bar area with data-draggable attribute
        if (e.target.closest('[data-draggable]')) {
          e.preventDefault();
          setIsDragging(true);
          const dialog = e.currentTarget;
          const rect = dialog.getBoundingClientRect();
          const offsetX = e.clientX - rect.left;
          const offsetY = e.clientY - rect.top;

          const handleMouseMove = (e) => {
            // Constrain to viewport bounds
            const maxX = window.innerWidth - dialog.offsetWidth;
            const maxY = window.innerHeight - dialog.offsetHeight;
            const newX = Math.max(0, Math.min(maxX, e.clientX - offsetX));
            const newY = Math.max(0, Math.min(maxY, e.clientY - offsetY));
            
            dialog.style.left = `${newX}px`;
            dialog.style.top = `${newY}px`;
            dialog.style.position = 'fixed';
            dialog.style.margin = '0';
            dialog.style.transform = 'none';
          };

          const handleMouseUp = () => {
            setIsDragging(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }
      }
    }}
  >
    <DialogTitle 
      data-draggable="true"
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1,
        borderBottom: 1,
        borderColor: 'divider',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-draggable="true">
        <Tooltip title="Drag to move window">
          <DragIndicator sx={{ color: 'text.secondary', cursor: 'grab' }} />
        </Tooltip>
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          <SmartToy />
        </Avatar>
        <Box data-draggable="true">
          <Typography variant="h6">
            MammoAssist™ AI Assistant
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Personalized breast health support for {user?.first_name || 'you'}
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Connection Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={`FastAPI: ${connectionStatus.fastapi}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Circle sx={{ 
                fontSize: 8, 
                color: getConnectionStatusColor(connectionStatus.fastapi) 
              }} />
              <Typography variant="caption" color="text.secondary">
                API
              </Typography>
            </Box>
          </Tooltip>
          
          <Tooltip title={`MCP Server: ${connectionStatus.mcp}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Circle sx={{ 
                fontSize: 8, 
                color: getConnectionStatusColor(connectionStatus.mcp) 
              }} />
              <Typography variant="caption" color="text.secondary">
                MCP
              </Typography>
            </Box>
          </Tooltip>
        </Box>
        
        {/* Add User Button */}
        <Tooltip title="Invite user to chat">
          <IconButton 
            size="small"
            onClick={(e) => setAddUserMenuAnchor(e.currentTarget)}
            aria-label="Invite user to chat"
          >
            <PersonAdd />
          </IconButton>
        </Tooltip>
        
        {/* Add Agent Button */}
        <Tooltip title="Add AI agent to chat">
          <IconButton 
            size="small"
            onClick={(e) => setAddAgentMenuAnchor(e.currentTarget)}
            aria-label="Add AI agent to chat"
          >
            <AddCircle />
          </IconButton>
        </Tooltip>
        
        {/* Minimize Button */}
        <Tooltip title="Minimize chat">
          <IconButton 
            onClick={handleMinimize}
            size="small"
            aria-label="Minimize MammoAssist chat dialog"
          >
            <Minimize />
          </IconButton>
        </Tooltip>
        
        {/* Close Button */}
        <IconButton 
          onClick={onClose}
          size="small"
          title="Close dialog"
          aria-label="Close MammoAssist chat dialog"
        >
          <Close />
        </IconButton>
      </Box>
    </DialogTitle>
    
    <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
      <MammoAssistChat 
        userRole={user?.primary_role}
        portalContext={currentPortal}
        compact={false}
        connectionStatus={connectionStatus}
      />
    </DialogContent>

    {/* Add User Menu */}
    <Menu
      anchorEl={addUserMenuAnchor}
      open={Boolean(addUserMenuAnchor)}
      onClose={() => setAddUserMenuAnchor(null)}
    >
      <MenuItem onClick={() => handleAddUser('doctor')}>
        <ListItemIcon><LocalHospital fontSize="small" /></ListItemIcon>
        <ListItemText primary="Invite Doctor" />
      </MenuItem>
      <MenuItem onClick={() => handleAddUser('nurse')}>
        <ListItemIcon><Person fontSize="small" /></ListItemIcon>
        <ListItemText primary="Invite Nurse" />
      </MenuItem>
      <MenuItem onClick={() => handleAddUser('patient')}>
        <ListItemIcon><Person fontSize="small" /></ListItemIcon>
        <ListItemText primary="Invite Patient" />
      </MenuItem>
      <MenuItem onClick={() => handleAddUser('caregiver')}>
        <ListItemIcon><Support fontSize="small" /></ListItemIcon>
        <ListItemText primary="Invite Caregiver" />
      </MenuItem>
    </Menu>

    {/* Add Agent Menu */}
    <Menu
      anchorEl={addAgentMenuAnchor}
      open={Boolean(addAgentMenuAnchor)}
      onClose={() => setAddAgentMenuAnchor(null)}
    >
      <MenuItem onClick={() => handleAddAgent('clinical')}>
        <ListItemIcon><Science fontSize="small" /></ListItemIcon>
        <ListItemText primary="Clinical Research Agent" />
      </MenuItem>
      <MenuItem onClick={() => handleAddAgent('diagnostic')}>
        <ListItemIcon><Analytics fontSize="small" /></ListItemIcon>
        <ListItemText primary="Diagnostic Agent" />
      </MenuItem>
      <MenuItem onClick={() => handleAddAgent('support')}>
        <ListItemIcon><Support fontSize="small" /></ListItemIcon>
        <ListItemText primary="Patient Support Agent" />
      </MenuItem>
      <MenuItem onClick={() => handleAddAgent('scheduling')}>
        <ListItemIcon><Event fontSize="small" /></ListItemIcon>
        <ListItemText primary="Scheduling Agent" />
      </MenuItem>
    </Menu>
  </Dialog>
  );
};

export default MammoAssistDialog;